import './styles.scss';

import FeatherIcon from 'feather-icons-react';
import { format } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

interface MaintenanceScheduledBannerProps {
  forceUnderway?: boolean;
}

const MaintenanceScheduledBanner = ({
  forceUnderway = false,
}: MaintenanceScheduledBannerProps): JSX.Element | null => {
  const flags = useFlags();
  const maintenanceScheduled = flags?.maintenanceScheduled as string | number | undefined | null;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [maintenanceScheduled]);

  const parsedDate = useMemo(() => {
    if (!maintenanceScheduled) return null;
    const date = new Date(maintenanceScheduled);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [maintenanceScheduled]);

  const underway = useMemo(() => {
    if (forceUnderway) return true;
    if (!parsedDate) return false;
    return new Date() > parsedDate;
  }, [forceUnderway, parsedDate]);

  const visible = Boolean(maintenanceScheduled) && (!dismissed || underway);

  const maintenanceDateTime = useMemo(() => {
    if (!maintenanceScheduled) return '';
    if (!parsedDate) return String(maintenanceScheduled);

    const formatted = format(parsedDate, "MMMM do 'at' h:mm a 'EST'");
    return formatted.replace(/\s*(AM|PM)\s*EST$/, (_, ampm) => `${ampm.toLowerCase()} EST`);
  }, [maintenanceScheduled, parsedDate]);

  if (!visible) return null;

  const title = underway ? 'Maintenance Underway' : 'Scheduled Maintenance';

  return (
    <div
      className={cx('maintenance-scheduled-banner', {
        'maintenance-scheduled-banner--underway': underway,
      })}
    >
      <div
        className={cx('maintenance-scheduled-banner__container', {
          'maintenance-scheduled-banner__container--scheduled': !underway,
        })}
      >
        <div className="maintenance-scheduled-banner__wrapper">
          <div className="maintenance-scheduled-banner__title">
            {underway ? (
              <FeatherIcon
                icon="alert-triangle"
                className="maintenance-scheduled-banner__title-icon"
              />
            ) : (
              <FeatherIcon
                icon="alert-circle"
                className="maintenance-scheduled-banner__title-icon"
              />
            )}
            <span className="maintenance-scheduled-banner__title-text">{title}</span>
          </div>
          <div
            className={cx('maintenance-scheduled-banner__separator', {
              'maintenance-scheduled-banner__separator--underway': underway,
            })}
          ></div>
          <div
            className={cx('maintenance-scheduled-banner__description', {
              'maintenance-scheduled-banner__description--underway': underway,
            })}
          >
            {underway ? (
              <>
                The platform is temporarily unavailable while we complete a system update. Estimated
                completion time is less than 1 hour. This message will disappear once the update is
                finished.
              </>
            ) : (
              <>
                INE will undergo scheduled maintenance on <b>{maintenanceDateTime}</b> for
                approximately 1 hour. During this time, the platform will be unavailable. Thank you
                for your patience.
              </>
            )}
          </div>
        </div>
      </div>
      {!underway && (
        <FeatherIcon
          icon="x"
          className="maintenance-scheduled-banner__close-icon"
          data-testid="maintenance-close"
          aria-label="Close maintenance banner"
          onClick={() => setDismissed(true)}
        />
      )}
    </div>
  );
};

export default MaintenanceScheduledBanner;
